import { Filter, PlusCircle, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import type { OrderRow, OrderStatus } from "./columns"; // Type-only imports
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState, useMemo, useCallback } from "react";

import { AdminLayout } from "@/layouts/admin-layout";
import { Button } from "@/components/ui/button";
import {
  Table as ShadcnTable, // Renamed to avoid conflict with Tanstack's Table type
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  type RowSelectionState,
} from "@tanstack/react-table";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
// Removed type { Table } import as useReactTable provides the table instance
import { getOrderColumns } from "./columns";
import { orderStatusEnum } from "@/server/db/schema/orders"; // For filter options
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useDebounce } from "@/hooks/use-debounce";
import { useTranslation } from "react-i18next";

import ViewOrderDialog from "@/components/admin/orders/ViewOrderDialog";
import EditOrderStatusDialog from "@/components/admin/orders/EditOrderStatusDialog";
// import CreateOrderDialog from "@/components/admin/orders/CreateOrderDialog"; // Placeholder for Create Order

const AdminOrdersPage = () => {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "all">("all");

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [viewOrderOpen, setViewOrderOpen] = useState(false);
  const [editStatusOpen, setEditStatusOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderRow | null>(null);
  // const [createOrderOpen, setCreateOrderOpen] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const { data: ordersData, isLoading, isError, error, refetch } = trpc.orders.listOrders.useQuery({ // Corrected: trpc.orders
    page,
    pageSize,
    search: debouncedSearchTerm || undefined,
    orderStatus: statusFilter === "all" ? undefined : statusFilter,
  });

  useEffect(() => {
    if (isError) {
      toast.error(t("admin.adminOrders.errors.loadFailed"), {
        description: error?.message,
      });
    }
  }, [isError, error, t]);



  const handleViewDetails = useCallback((orderId: string) => {
    const order = ordersData?.items.find(o => o.id === orderId);
    if (order) {
      setSelectedOrder(order as unknown as OrderRow); // Cast needed due to trpc type
      setViewOrderOpen(true);
    }
    // toast.info(`View details for order ID: ${orderId} (Not Implemented)`);
  }, [ordersData?.items]);

  const handleEditStatus = useCallback((order: OrderRow) => {
    setSelectedOrder(order);
    setEditStatusOpen(true);
    // toast.info(`Edit status for order: ${order.orderNumber} (Not Implemented)`);
  }, []);

  // const handleCreateOrder = useCallback(() => {
  //   setCreateOrderOpen(true);
  // }, []);

  const handleCancelOrder = useCallback((orderId: string, orderNumber: string) => {
    // Placeholder for actual cancel logic
    // Potentially open a confirmation dialog here
    toast.warning(t("admin.adminOrders.messages.cancelOrderPrompt", { orderNumber }), {
      description: t("admin.adminOrders.messages.cancelOrderPromptDescription", `Order ID: ${orderId}. This action is not yet implemented.`),
      action: {
        label: t("common.confirm", "Confirm"),
        onClick: () => toast.info(t("admin.adminOrders.messages.cancelConfirmedNotImplemented", { orderNumber })),
      },
    });
  }, [t]);

  const markOrderAsPaidMutation = trpc.orders.markOrderAsPaid.useMutation({
    onSuccess: (data) => {
      toast.success(t("admin.adminOrders.messages.markedAsPaid", { orderNumber: data.orderNumber }));
      utils.orders.listOrders.invalidate();
    },
    onError: (error) => {
      toast.error(t("admin.adminOrders.errors.markAsPaidFailed"), {
        description: error.message,
      });
    },
  });

  const handleMarkAsPaid = useCallback((orderId: string, orderNumber: string) => {
    // Future: Add a confirmation dialog here if desired
    // For now, directly call the mutation
    toast.info(t("admin.adminOrders.messages.markingAsPaid", { orderNumber }));
    markOrderAsPaidMutation.mutate({ id: orderId }); // Changed orderId to id
  }, [t, markOrderAsPaidMutation]); // Removed utils.orders.listOrders from dependencies

  const columns = useMemo(
    () => getOrderColumns(t, handleViewDetails, handleEditStatus, handleCancelOrder, handleMarkAsPaid),
    [t, handleViewDetails, handleEditStatus, handleCancelOrder, handleMarkAsPaid]
  );

  const pageCount = ordersData?.total ? Math.ceil(ordersData.total / pageSize) : 0;

  const table = useReactTable({
    data: (ordersData?.items as OrderRow[] ?? []),
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: true,
    manualPagination: true,
    state: {
      rowSelection,
      pagination: {
        pageIndex: page - 1,
        pageSize,
      },
    },
    onRowSelectionChange: setRowSelection,
    pageCount,
  });

  const selectedRowsData = table.getFilteredSelectedRowModel().rows.map((row) => row.original);

  // Use the updated enum values directly from the imported schema
  const orderStatusOptions = ["all", ...orderStatusEnum.enumValues] as const;

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">{t("admin.adminOrders.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("admin.adminOrders.description")}</p>
        </div>
        {/* <Button onClick={handleCreateOrder}>
          <PlusCircle className="mr-2 h-4 w-4" />
          {t("admin.adminOrders.actions.createOrder")}
        </Button> */}
      </div>
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder={t("admin.adminOrders.filters.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 w-full sm:w-72"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as OrderStatus | "all")}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder={t("admin.adminOrders.filters.statusPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            {orderStatusOptions.map((status) => (
              <SelectItem key={status} value={status}>
                {status === "all"
                  ? t("admin.adminOrders.filters.allStatuses")
                  : t(`enums.orderStatus.${status}`, status.replace(/_/g, " "))}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-6">
        {selectedRowsData.length > 0 && (
          <div className="flex items-center space-x-2">
            <span className="text-sm">{selectedRowsData.length} {t("common.rowsSelected", { count: selectedRowsData.length })}</span>
            {/* Placeholder for bulk actions */}
          </div>
        )}

        <div className="bg-card rounded-md border shadow-sm overflow-hidden">
          <ShadcnTable>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="bg-muted/50">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="font-medium py-4">
                      {header.isPlaceholder ? null : (
                        <div>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: pageSize }).map((_, rowIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                  <TableRow key={`skel-row-p${page}-${rowIndex}`}>
                    {columns.map((column, colIndex) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                      <TableCell key={`skel-cell-p${page}-r${rowIndex}-c${colIndex}`} className="py-3">
                        <Skeleton className={`h-5 w-${colIndex === 0 ? '4' : colIndex === 1 || colIndex === 2 ? '32' : '20'} rounded`} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className="hover:bg-muted/50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="py-3">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    {t("admin.adminOrders.noOrdersFound")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </ShadcnTable>
        </div>

        <div className="flex items-center justify-between px-1 sm:px-4">
          <div className="flex-1 text-sm text-muted-foreground">
            {table.getFilteredSelectedRowModel().rows.length} {t("common.of")} {table.getFilteredRowModel().rows.length} {t("common.rowsSelected", { count: table.getFilteredSelectedRowModel().rows.length})}
          </div>
          <div className="flex items-center space-x-2 sm:space-x-6 lg:space-x-8">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium hidden sm:inline">
                {t("common.rowsPerPage")}
              </span>
              <Select
                value={`${pageSize}`}
                onValueChange={(value) => {
                  const newPageSize = Number(value);
                  setPageSize(newPageSize);
                  table.setPageSize(newPageSize);
                  setPage(1); // Reset to first page
                  table.setPageIndex(0);
                }}
              >
                <SelectTrigger className="h-8 w-16 sm:w-20">
                  <SelectValue placeholder={pageSize} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 20, 30, 40, 50].map((size) => (
                    <SelectItem key={size} value={`${size}`}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex w-[100px] sm:w-[120px] items-center justify-center text-sm font-medium">
              {t("common.page")} {page} {t("common.of")} {pageCount || 1}
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => { setPage(1); table.setPageIndex(0); }}
                disabled={page === 1}
              >
                <span className="sr-only">{t("common.firstPage")}</span>
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => { setPage(p => p - 1); table.previousPage(); }}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">{t("common.previousPage")}</span>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => { setPage(p => p + 1); table.nextPage(); }}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">{t("common.nextPage")}</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => { setPage(pageCount || 1); table.setPageIndex((pageCount || 1) -1); }}
                disabled={page === (pageCount || 1)}
              >
                <span className="sr-only">{t("common.lastPage")}</span>
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {selectedOrder && (
        <ViewOrderDialog
          isOpen={viewOrderOpen}
          onOpenChange={setViewOrderOpen}
          order={selectedOrder}
        />
      )}
      {selectedOrder && (
        <EditOrderStatusDialog
          isOpen={editStatusOpen}
          onOpenChange={setEditStatusOpen}
          order={selectedOrder}
          onSuccess={() => {
            utils.orders.listOrders.invalidate(); // Corrected: utils.orders
            toast.success(t("admin.adminOrders.messages.statusUpdated"));
          }}
        />
      )}
      {/* <CreateOrderDialog
        isOpen={createOrderOpen}
        onOpenChange={setCreateOrderOpen}
        onSuccess={() => {
          utils.orders.listOrders.invalidate();
          toast.success(t("admin.adminOrders.messages.orderCreated")); // Assuming a translation key
        }}
      /> */}
    </AdminLayout>
  );
};

export default AdminOrdersPage;